import { TotalResults } from '@shared/components/ui/TotalResults';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { SortConfig } from '@components/ui/VirtualTable';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { useShops } from '../../hooks/useShops';
import { CardList, ShopsAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { AddShopButton, FiltersSection } from '../interaction';

export const Main = () => {
  const { t } = useTranslation('shopsModule');
  const queryClient = useQueryClient();
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });
  const [searchTerm, setSearchTerm] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [sorting, setSorting] = useState<SortConfig | null>(() => ({
    field: queryParams.sortBy || 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
  }));

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    setSearchTerm(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  useEffect(() => {
    const nextSorting: SortConfig = {
      field: queryParams.sortBy || 'id',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'desc',
    };

    setSorting((prev) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // 🍞 Breadcrumbs (el botón Historial va uno por cada shop en la lista)
  const breadcrumbs = useMemo(() => [{ label: t('breadcrumb.tiendas', 'Tiendas') }], [t]);
  usePageNavigation({ breadcrumbs });

  // 🎯 Procesar filtros para usar advanced_filters (siguiendo convenciones del proyecto)
  // Cambio realizado: Migrar de parámetros individuales (status, channel_id) a advanced_filters
  // para mantener consistencia con el resto del proyecto y permitir filtros más flexibles
  const processedFilters = useMemo(() => {
    const filters: any = {};

    // 🎯 Construir advanced_filters si hay condiciones de filtro
    if (filterConditions.length > 0) {
      const advancedFilters = {
        filters: filterConditions.map((condition) => ({
          column: condition.column,
          operator: condition.action,
          value: condition.value,
        })),
        logic: 'AND' as const,
      };
      filters.advanced_filters = advancedFilters;
    }

    // 🎯 Agregar parámetros de ordenamiento
    filters.sortBy = sorting?.field ?? 'id';
    filters.sortDirection = sorting?.direction ?? 'desc';

    return filters;
  }, [filterConditions, sorting]);

  const handleFilterChange = useCallback((conditions: FilterConditionOutput[]) => {
    setFilterConditions(conditions);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortConfig | null) => {
      if (!newSorting || !newSorting.field || !newSorting.direction) {
        const defaultSorting: SortConfig = { field: 'id', direction: 'desc' };
        setSorting(defaultSorting);

        if (queryParams.sortBy !== defaultSorting.field || queryParams.sortDirection !== defaultSorting.direction) {
          updateQueryParams({
            sortBy: defaultSorting.field,
            sortDirection: defaultSorting.direction,
          });
        }
        return;
      }

      setSorting(newSorting);

      if (newSorting.field !== queryParams.sortBy || newSorting.direction !== queryParams.sortDirection) {
        updateQueryParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      }
    },
    [queryParams.sortBy, queryParams.sortDirection, updateQueryParams],
  );

  // 🎯 Hook con scroll infinito + filtros específicos para tiendas
  const {
    shops,
    isLoading,
    isFetching, // 🎯 Para aplicar opacidad durante refetch
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage, // 🎯 Para detectar infinite scroll
    total,
  } = useShops({
    searchTerm: searchTerm,
    ...processedFilters, // ✅ Campos específicos (status, channel_id)
  });

  const handleSearch = useCallback((searchTerm: string) => {
    setSearchTerm(searchTerm);
  }, []);

  // Función para refrescar la lista de tiendas después de crear una nueva
  const handleShopCreated = useCallback(async () => {
    // Invalidar todas las queries de shops para forzar el refetch
    await queryClient.invalidateQueries({
      queryKey: ['shops'],
      exact: false,
    });
  }, [queryClient]);

  // 🎯 Hook para manejar las columnas de AG Grid
  const { tanStackColumns } = useAgGridColumns({
    hasTeamData: Array.isArray(shops) ? shops.some((s: any) => Object.prototype.hasOwnProperty.call(s, 'team')) : false,
    selectedColumns: [], // TODO: Implementar selector de columnas si es necesario
  });

  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  // Props compartidas para ambas vistas (DRY principle)
  const shopsViewProps = useMemo(
    () => ({
      shops,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch, // 🎯 Agregar refetch para actualizar lista después de cambios
    }),
    [shops, fetchNextPage, hasNextPage, isFetchingNextPage, total, refetch],
  );

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Funcion que decide que vista renderizar (Responsabilidad movida desde Table)
  const renderShops = () => {
    if (isMobile) {
      return <CardList {...shopsViewProps} showActions={true} />;
    }

    // 🎯 Usar AG Grid para la vista de escritorio (sin selección múltiple)
    return (
      <ShopsAgGridTable
        shops={shops}
        isFetching={isFetching} // 🎯 Para aplicar opacidad
        refetch={refetch}
        selectedColumns={[]} // TODO: Implementar selector de columnas
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad && isLoading) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Header con filtros y botón crear */}
      <div className="mb-4">
        <div className="flex sm:items-center sm:justify-between flex-wrap gap-3 mb-4 flex-col md:flex-row">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection handleSearch={handleSearch} onFilterChange={handleFilterChange} />
          </div>
          <TotalResults total={total} />
          {/* Botón crear cuando hay datos O cuando hay búsqueda/filtros activos */}
          {(shops.length > 0 || searchTerm || filterConditions.length > 0) && (
            <div className="shrink-0 absolute right-4 top-2">
              <AddShopButton onShopCreated={handleShopCreated} />
            </div>
          )}
        </div>
      </div>

      {/* Vista de tiendas: siempre en el DOM, controlado por opacity */}
      <div
        className={`mb-4 transition-opacity duration-200 ${
          shops.length > 0 ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        } ${
          // Durante búsquedas (no primera carga): opacity-50
          !isInitialLoad && isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        {renderShops()}
      </div>

      {/* Estado vacío: solo cuando no hay datos Y no es carga inicial */}
      {!isInitialLoad && shops.length === 0 && (
        <EmptyView
          type={searchTerm || filterConditions.length > 0 ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.SHOPS}
          customAction={
            // Mostrar botón en EmptyView cuando NO hay búsqueda/filtros
            searchTerm || filterConditions.length > 0 ? null : (
              <div className="flex justify-center">
                <AddShopButton onShopCreated={handleShopCreated} />
              </div>
            )
          }
        />
      )}
    </div>
  );
};
